import {computed, inject, Injectable, signal} from '@angular/core';
import {collection, Firestore, getDocs, onSnapshot, query, where,} from '@angular/fire/firestore';
import {AuthStore} from '../auth/auth.store';

@Injectable({ providedIn: 'root' })
export class ApprovalCountService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);

  private readonly _pendingTimesheetCount = signal(0);
  private readonly _pendingLeaveCount = signal(0);
  private readonly _pendingExpenseCount = signal(0);
  private readonly _loading = signal(false);

  private timesheetUnsubscribes: Array<() => void> = [];
  private leaveUnsubscribes: Array<() => void> = [];
  private expenseUnsubscribes: Array<() => void> = [];

  readonly pendingTimesheetCount = this._pendingTimesheetCount.asReadonly();
  readonly pendingLeaveCount = this._pendingLeaveCount.asReadonly();
  readonly pendingExpenseCount = this._pendingExpenseCount.asReadonly();
  readonly loading = this._loading.asReadonly();

  readonly totalPendingCount = computed(
    () =>
      this._pendingTimesheetCount() +
      this._pendingLeaveCount() +
      this._pendingExpenseCount()
  );

  private get tenantId(): string | null {
    return this.authStore.tenantId();
  }

  private get userRole(): string | null {
    return this.authStore.userRole();
  }

  async subscribe(): Promise<void> {
    const tenantId = this.tenantId;
    const role = this.userRole;

    if (!tenantId) return;

    // Only managers and hr_admins can see pending approvals
    if (role !== 'manager' && role !== 'hr_admin' && role !== 'finance_admin') {
      this._pendingTimesheetCount.set(0);
      this._pendingLeaveCount.set(0);
      this._pendingExpenseCount.set(0);
      return;
    }

    this._loading.set(true);

    try {
      await this.subscribeToTimesheets(tenantId, role);
      await this.subscribeToLeaveRequests(tenantId, role);
      await this.subscribeToExpenses(tenantId, role);
    } finally {
      this._loading.set(false);
    }
  }

  private async subscribeToTimesheets(tenantId: string, role: string): Promise<void> {
    // Clean up previous subscriptions
    this.timesheetUnsubscribes.forEach((unsub) => unsub());
    this.timesheetUnsubscribes = [];

    const timesheetsRef = collection(this.firestore, `tenants/${tenantId}/timesheets`);

    if (role === 'hr_admin') {
      // HR admins see all submitted timesheets
      const timesheetQuery = query(timesheetsRef, where('status', '==', 'submitted'));
      const unsub = onSnapshot(
        timesheetQuery,
        (snapshot) => {
          this._pendingTimesheetCount.set(snapshot.size);
        },
        (error) => {
          console.error('Timesheet count subscription error:', error);
          this._pendingTimesheetCount.set(0);
        }
      );
      this.timesheetUnsubscribes.push(unsub);
    } else {
      // Managers see timesheets assigned to them + need to count unassigned from direct reports
      const approverEmployeeId = await this.getEmployeeIdForCurrentUser(tenantId);
      if (!approverEmployeeId) {
        this._pendingTimesheetCount.set(0);
        return;
      }

      // Get direct reports
      const directReportUserIds = await this.getDirectReportUserIds(tenantId, approverEmployeeId);

      let directCount = 0;
      let unassignedCount = 0;

      // Subscribe to directly assigned timesheets
      const directQuery = query(
        timesheetsRef,
        where('status', '==', 'submitted'),
        where('approverEmployeeId', '==', approverEmployeeId)
      );
      const directUnsub = onSnapshot(
        directQuery,
        (snapshot) => {
          directCount = snapshot.size;
          this._pendingTimesheetCount.set(directCount + unassignedCount);
        },
        (error) => {
          console.error('Timesheet count subscription error:', error);
        }
      );
      this.timesheetUnsubscribes.push(directUnsub);

      // Subscribe to unassigned timesheets from direct reports
      if (directReportUserIds.length > 0) {
        // Firestore 'in' queries support up to 30 items
        const chunks = this.chunkArray(directReportUserIds, 30);
        for (const chunk of chunks) {
          const unassignedNullQuery = query(
            timesheetsRef,
            where('status', '==', 'submitted'),
            where('approverEmployeeId', '==', null),
            where('userId', 'in', chunk)
          );
          const unassignedEmptyQuery = query(
            timesheetsRef,
            where('status', '==', 'submitted'),
            where('approverEmployeeId', '==', ''),
            where('userId', 'in', chunk)
          );

          const nullUnsub = onSnapshot(
            unassignedNullQuery,
            (snapshot) => {
              unassignedCount = snapshot.size;
              this._pendingTimesheetCount.set(directCount + unassignedCount);
            },
            () => {}
          );
          const emptyUnsub = onSnapshot(
            unassignedEmptyQuery,
            (snapshot) => {
              unassignedCount += snapshot.size;
              this._pendingTimesheetCount.set(directCount + unassignedCount);
            },
            () => {}
          );
          this.timesheetUnsubscribes.push(nullUnsub, emptyUnsub);
        }
      }
    }
  }

  private async subscribeToLeaveRequests(tenantId: string, role: string): Promise<void> {
    // Clean up previous subscriptions
    this.leaveUnsubscribes.forEach((unsub) => unsub());
    this.leaveUnsubscribes = [];

    const leaveRef = collection(this.firestore, `tenants/${tenantId}/leaveRequests`);

    if (role === 'hr_admin') {
      // HR admins see all pending leave requests
      const leaveQuery = query(leaveRef, where('status', '==', 'pending'));
      const unsub = onSnapshot(
        leaveQuery,
        (snapshot) => {
          this._pendingLeaveCount.set(snapshot.size);
        },
        (error) => {
          console.error('Leave count subscription error:', error);
          this._pendingLeaveCount.set(0);
        }
      );
      this.leaveUnsubscribes.push(unsub);
    } else {
      // Managers see requests assigned to them + unassigned from direct reports
      const approverEmployeeId = await this.getEmployeeIdForCurrentUser(tenantId);
      if (!approverEmployeeId) {
        this._pendingLeaveCount.set(0);
        return;
      }

      // Get direct reports
      const directReportUserIds = await this.getDirectReportUserIds(tenantId, approverEmployeeId);

      let directCount = 0;
      let unassignedCount = 0;

      // Subscribe to directly assigned leave requests
      const directQuery = query(
        leaveRef,
        where('status', '==', 'pending'),
        where('approverEmployeeId', '==', approverEmployeeId)
      );
      const directUnsub = onSnapshot(
        directQuery,
        (snapshot) => {
          directCount = snapshot.size;
          this._pendingLeaveCount.set(directCount + unassignedCount);
        },
        (error) => {
          console.error('Leave count subscription error:', error);
        }
      );
      this.leaveUnsubscribes.push(directUnsub);

      // Subscribe to unassigned leave requests from direct reports
      if (directReportUserIds.length > 0) {
        const chunks = this.chunkArray(directReportUserIds, 30);
        for (const chunk of chunks) {
          const unassignedNullQuery = query(
            leaveRef,
            where('status', '==', 'pending'),
            where('approverEmployeeId', '==', null),
            where('userId', 'in', chunk)
          );
          const unassignedEmptyQuery = query(
            leaveRef,
            where('status', '==', 'pending'),
            where('approverEmployeeId', '==', ''),
            where('userId', 'in', chunk)
          );

          const nullUnsub = onSnapshot(
            unassignedNullQuery,
            (snapshot) => {
              unassignedCount = snapshot.size;
              this._pendingLeaveCount.set(directCount + unassignedCount);
            },
            () => {}
          );
          const emptyUnsub = onSnapshot(
            unassignedEmptyQuery,
            (snapshot) => {
              unassignedCount += snapshot.size;
              this._pendingLeaveCount.set(directCount + unassignedCount);
            },
            () => {}
          );
          this.leaveUnsubscribes.push(nullUnsub, emptyUnsub);
        }
      }
    }
  }

  private async subscribeToExpenses(tenantId: string, role: string): Promise<void> {
    this.expenseUnsubscribes.forEach((unsub) => unsub());
    this.expenseUnsubscribes = [];

    const expenseRef = collection(this.firestore, `tenants/${tenantId}/expenseClaims`);

    if (role === 'hr_admin' || role === 'finance_admin') {
      const expenseQuery = query(expenseRef, where('status', '==', 'submitted'));
      const unsub = onSnapshot(
        expenseQuery,
        (snapshot) => {
          this._pendingExpenseCount.set(snapshot.size);
        },
        (error) => {
          console.error('Expense count subscription error:', error);
          this._pendingExpenseCount.set(0);
        }
      );
      this.expenseUnsubscribes.push(unsub);
      return;
    }

    const approverEmployeeId = await this.getEmployeeIdForCurrentUser(tenantId);
    if (!approverEmployeeId) {
      this._pendingExpenseCount.set(0);
      return;
    }

    const directReportUserIds = await this.getDirectReportUserIds(tenantId, approverEmployeeId);

    let directCount = 0;
    let unassignedCount = 0;

    const directQuery = query(
      expenseRef,
      where('status', '==', 'submitted'),
      where('approverEmployeeId', '==', approverEmployeeId)
    );
    const directUnsub = onSnapshot(
      directQuery,
      (snapshot) => {
        directCount = snapshot.size;
        this._pendingExpenseCount.set(directCount + unassignedCount);
      },
      (error) => {
        console.error('Expense count subscription error:', error);
      }
    );
    this.expenseUnsubscribes.push(directUnsub);

    if (directReportUserIds.length > 0) {
      const chunks = this.chunkArray(directReportUserIds, 30);
      for (const chunk of chunks) {
        const unassignedNullQuery = query(
          expenseRef,
          where('status', '==', 'submitted'),
          where('approverEmployeeId', '==', null),
          where('userId', 'in', chunk)
        );
        const unassignedEmptyQuery = query(
          expenseRef,
          where('status', '==', 'submitted'),
          where('approverEmployeeId', '==', ''),
          where('userId', 'in', chunk)
        );

        const nullUnsub = onSnapshot(
          unassignedNullQuery,
          (snapshot) => {
            unassignedCount = snapshot.size;
            this._pendingExpenseCount.set(directCount + unassignedCount);
          },
          () => {}
        );
        const emptyUnsub = onSnapshot(
          unassignedEmptyQuery,
          (snapshot) => {
            unassignedCount += snapshot.size;
            this._pendingExpenseCount.set(directCount + unassignedCount);
          },
          () => {}
        );
        this.expenseUnsubscribes.push(nullUnsub, emptyUnsub);
      }
    }
  }

  private async getEmployeeIdForCurrentUser(tenantId: string): Promise<string | null> {
    const userEmail = this.authStore.user()?.email?.toLowerCase();
    if (!userEmail) return null;

    const employeesRef = collection(this.firestore, `tenants/${tenantId}/employees`);
    const employeeQuery = query(employeesRef, where('email', '==', userEmail));
    const snap = await getDocs(employeeQuery);
    return snap.docs[0]?.id ?? null;
  }

  private async getDirectReportUserIds(tenantId: string, managerEmployeeId: string): Promise<string[]> {
    try {
      // Find all employees who have this manager
      const employeesRef = collection(this.firestore, `tenants/${tenantId}/employees`);
      const directReportsQuery = query(employeesRef, where('managerId', '==', managerEmployeeId));
      const snap = await getDocs(directReportsQuery);

      // Get the user IDs for each direct report by looking up their email
      const userIds: string[] = [];
      for (const docSnap of snap.docs) {
        const email = docSnap.data()['email'] as string | undefined;
        if (!email) continue;

        // Find the user with this email
        const usersRef = collection(this.firestore, `tenants/${tenantId}/users`);
        const userQuery = query(usersRef, where('email', '==', email.toLowerCase()));
        const userSnap = await getDocs(userQuery);
        if (userSnap.docs[0]) {
          userIds.push(userSnap.docs[0].id);
        }
      }

      return userIds;
    } catch (error) {
      console.error('Failed to get direct report user IDs:', error);
      return [];
    }
  }

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  unsubscribe(): void {
    this.timesheetUnsubscribes.forEach((unsub) => unsub());
    this.timesheetUnsubscribes = [];
    this.leaveUnsubscribes.forEach((unsub) => unsub());
    this.leaveUnsubscribes = [];
    this.expenseUnsubscribes.forEach((unsub) => unsub());
    this.expenseUnsubscribes = [];
    this._pendingTimesheetCount.set(0);
    this._pendingLeaveCount.set(0);
    this._pendingExpenseCount.set(0);
  }
}
